import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { tryonAPI, lookbookAPI, clientsAPI, fabricsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { TryOnImageLightbox } from '@/components/TryOnImageLightbox';
import {
  Plus, Sparkles, Image as ImageIcon, Send, Eye, RefreshCw, Check, X,
  ThumbsUp, ThumbsDown, Loader2, Users, BarChart3, IndianRupee, Zap,
  Camera, ChevronRight, Star, MessageSquare, Clock, Filter, Search,
  Archive, UserPlus, Trash2, Copy, MessageCircle,
} from 'lucide-react';
import { HelpTooltip } from '@/components/HelpTooltip';

const STATUS_COLORS = {
  draft: { bg: 'rgba(107,114,128,0.1)', text: '#6B7280' },
  generating: { bg: 'rgba(59,130,246,0.1)', text: '#2563EB' },
  review: { bg: 'rgba(245,158,11,0.1)', text: '#D97706' },
  sending: { bg: 'rgba(139,92,246,0.1)', text: '#7C3AED' },
  sent: { bg: 'rgba(34,197,94,0.1)', text: '#16A34A' },
  completed: { bg: 'rgba(34,197,94,0.1)', text: '#16A34A' },
  failed: { bg: 'rgba(239,68,68,0.1)', text: '#DC2626' },
};

const RATING_OPTIONS = [
  { value: 'excellent', label: 'Excellent', color: '#16A34A' },
  { value: 'good', label: 'Good', color: '#059669' },
  { value: 'acceptable', label: 'Acceptable', color: '#D97706' },
  { value: 'poor', label: 'Poor', color: '#EA580C' },
  { value: 'rejected', label: 'Rejected', color: '#DC2626' },
];

export default function LookbooksPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('campaigns');
  const [campaigns, setCampaigns] = useState([]);
  const [images, setImages] = useState([]);
  const [archiveImages, setArchiveImages] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxImages, setLightboxImages] = useState([]);
  
  // Create campaign dialog
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newCampaign, setNewCampaign] = useState({
    campaign_name: '',
    campaign_type: 'seasonal',
    looks_per_client: 3,
    occasion_context: '',
    client_ids: [],
  });
  
  // Client search for campaign
  const [clientSearch, setClientSearch] = useState('');
  const [clientSearchResults, setClientSearchResults] = useState([]);
  const [clientSearchLoading, setClientSearchLoading] = useState(false);
  const [selectedClients, setSelectedClients] = useState([]);
  
  // Archive assign dialog
  const [showAssign, setShowAssign] = useState(false);
  const [assignArchiveId, setAssignArchiveId] = useState(null);
  const [assignSearch, setAssignSearch] = useState('');
  const [assignSearchResults, setAssignSearchResults] = useState([]);
  const [assignSearchLoading, setAssignSearchLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  
  // Stats
  const [stats, setStats] = useState({ total_campaigns: 0, total_images: 0, total_sent: 0 });
  const [generationLimits, setGenerationLimits] = useState({ max_looks_per_client: 3, max_clients_per_batch: 10 });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [campaignsRes, imagesRes, archiveRes] = await Promise.all([
        lookbookAPI.listCampaigns({ limit: 50 }),
        tryonAPI.listImages({ limit: 100 }),
        tryonAPI.listArchive({ limit: 50 }),
      ]);
      setCampaigns(campaignsRes.data || []);
      setImages(imagesRes.data || []);
      setArchiveImages(archiveRes.data || []);
      
      const c = campaignsRes.data || [];
      setStats({
        total_campaigns: c.length,
        total_images: c.reduce((s, x) => s + (x.total_images_generated || 0), 0),
        total_sent: c.reduce((s, x) => s + (x.total_sent || 0), 0),
        total_enquiries: c.reduce((s, x) => s + (x.enquiry_count || 0), 0),
        total_revenue: c.reduce((s, x) => s + (x.revenue_attributed || 0), 0),
      });

      // Fetch generation limits
      try {
        const configRes = await tryonAPI.getConfig();
        if (configRes.data) {
          setGenerationLimits({
            max_looks_per_client: configRes.data.max_looks_per_client || 3,
            max_clients_per_batch: configRes.data.max_clients_per_batch || 10,
          });
        }
      } catch {}
    } catch (err) {
      toast.error('Failed to load lookbook data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // Client search for campaign creation
  useEffect(() => {
    if (clientSearch.length < 2) { setClientSearchResults([]); return; }
    const timer = setTimeout(async () => {
      setClientSearchLoading(true);
      try {
        const res = await clientsAPI.list({ search: clientSearch, limit: 15 });
        setClientSearchResults(res.data.clients || []);
      } catch (err) { console.error(err); }
      finally { setClientSearchLoading(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [clientSearch]);

  // Archive assign client search
  useEffect(() => {
    if (assignSearch.length < 2) { setAssignSearchResults([]); return; }
    const timer = setTimeout(async () => {
      setAssignSearchLoading(true);
      try {
        const res = await clientsAPI.list({ search: assignSearch, limit: 10 });
        setAssignSearchResults(res.data.clients || []);
      } catch (err) { console.error(err); }
      finally { setAssignSearchLoading(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [assignSearch]);

  const toggleClientSelection = (client) => {
    setSelectedClients(prev => {
      const exists = prev.find(c => c.id === client.id);
      if (exists) return prev.filter(c => c.id !== client.id);
      if (prev.length >= generationLimits.max_clients_per_batch) {
        toast.error(`Maximum ${generationLimits.max_clients_per_batch} clients per batch. Create multiple campaigns for larger groups.`);
        return prev;
      }
      return [...prev, client];
    });
  };

  const removeSelectedClient = (clientId) => {
    setSelectedClients(prev => prev.filter(c => c.id !== clientId));
  };

  const handleCreateCampaign = async () => {
    if (!newCampaign.campaign_name.trim()) {
      toast.error('Please enter a campaign name');
      return;
    }
    if (selectedClients.length === 0) {
      toast.error('Please select at least one client');
      return;
    }
    setCreating(true);
    try {
      await lookbookAPI.createCampaign({
        ...newCampaign,
        client_ids: selectedClients.map(c => c.id),
      });
      toast.success('Campaign created');
      setShowCreate(false);
      setNewCampaign({ campaign_name: '', campaign_type: 'seasonal', looks_per_client: 3, occasion_context: '', client_ids: [] });
      setSelectedClients([]);
      setClientSearch('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create campaign');
    } finally {
      setCreating(false);
    }
  };

  const handleStartGeneration = async (campaignId) => {
    try {
      const configRes = await tryonAPI.getConfig();
      const config = configRes.data;
      if (!config.gemini_api_key && !config.gemini_api_key_masked) {
        toast.error('Gemini API key not configured. Go to Settings > Try-On Configuration to add your API key.', { duration: 6000 });
        return;
      }
      
      await lookbookAPI.startGeneration(campaignId);
      toast.success('Generation started! This may take a while...');
      
      const pollInterval = setInterval(async () => {
        try {
          const updated = await lookbookAPI.getCampaign(campaignId);
          if (updated.data.status !== 'generating') {
            clearInterval(pollInterval);
            if (updated.data.status === 'failed') {
              toast.error(`Generation failed: ${updated.data.error || 'Unknown error'}`, { duration: 8000 });
            } else if (updated.data.total_images_generated > 0) {
              toast.success(`Generated ${updated.data.total_images_generated} images!`);
            }
            fetchData();
          }
        } catch (e) { clearInterval(pollInterval); }
      }, 5000);
      
      setTimeout(() => clearInterval(pollInterval), 300000);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start generation', { duration: 6000 });
    }
  };

  const handleRateImage = async (imageId, rating) => {
    try {
      await tryonAPI.rateImage(imageId, rating);
      toast.success(`Image rated as ${rating}`);
      fetchData();
    } catch (err) { toast.error('Failed to rate image'); }
  };

  const openLightbox = (imageList, index) => {
    setLightboxImages(imageList);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const handleApproveImage = async (imageId) => {
    await tryonAPI.rateImage(imageId, 'excellent');
    fetchData();
  };

  const handleRejectImage = async (imageId) => {
    await tryonAPI.rateImage(imageId, 'rejected');
    fetchData();
  };

  const handleRegenerateImage = async (image) => {
    try {
      await tryonAPI.generate({
        client_id: image.client_id,
        garment_type: image.garment_type,
        fabric_description: image.fabric_description,
      });
      toast.success('Regeneration started');
      fetchData();
    } catch (err) {
      toast.error('Failed to regenerate');
      throw err;
    }
  };

  // Archive actions
  const handleDeleteArchive = async (archiveId) => {
    try {
      await tryonAPI.deleteArchive(archiveId);
      toast.success('Image removed from archive');
      setArchiveImages(prev => prev.filter(i => i.id !== archiveId));
    } catch (err) { toast.error('Failed to delete'); }
  };

  const handleAssignArchive = async (clientId) => {
    if (!assignArchiveId || !clientId) return;
    setAssigning(true);
    try {
      const res = await tryonAPI.assignArchiveToClient(assignArchiveId, clientId);
      toast.success(res.data.message || 'Image assigned to client');
      setShowAssign(false);
      setAssignArchiveId(null);
      setAssignSearch('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign');
    } finally { setAssigning(false); }
  };

  const handleCopyImage = async (imageUrl) => {
    try {
      if (imageUrl?.startsWith('data:')) {
        const res = await fetch(imageUrl);
        const blob = await res.blob();
        await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
        toast.success('Image copied to clipboard');
      } else {
        toast.error('Cannot copy this image format');
      }
    } catch (err) {
      toast.error('Copy failed — try downloading instead');
    }
  };

  const handleWhatsAppShare = async (image) => {
    const clientName = image.client_name || 'there';
    const garment = image.garment_type?.replace(/_/g, ' ') || 'garment';
    const fabric = image.fabric_description || image.fabric_code || 'premium fabric';
    const message = `Hi ${clientName}! Here's a preview of your ${garment} in ${fabric}. What do you think? - Herringbone & Sui`;
    
    const phone = image.client_phone || '';
    const waUrl = phone ? `https://wa.me/${phone.replace(/\D/g, '')}?text=${encodeURIComponent(message)}` : `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(waUrl, '_blank');
    toast.success('Opening WhatsApp...');
  };

  return (
    <div className="space-y-6 p-6" data-testid="lookbooks-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
            <Sparkles className="w-6 h-6" style={{ color: 'var(--brand-gold)' }} />
            AI Virtual Try-On & Lookbooks
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--ink-muted)' }}>
            Generate photorealistic images of clients wearing your garments
          </p>
        </div>
        <div className="flex items-center gap-2">
          <HelpTooltip text="Create personalised lookbooks with AI try-on images. Select clients and fabrics, then generate." testId="help-lookbooks" />
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button size="sm" onClick={() => setShowCreate(true)} className="gap-1"
            style={{ backgroundColor: 'var(--brand-gold)' }} data-testid="create-campaign-btn">
            <Plus className="w-4 h-4" /> New Campaign
          </Button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { icon: BarChart3, label: 'Campaigns', value: stats.total_campaigns, color: 'var(--brand-navy)', bg: 'rgba(27,42,74,0.08)' },
          { icon: ImageIcon, label: 'Images Generated', value: stats.total_images, color: 'var(--brand-gold)', bg: 'rgba(197,165,90,0.15)' },
          { icon: Send, label: 'Sent', value: stats.total_sent, color: '#16A34A', bg: 'rgba(34,197,94,0.1)' },
          { icon: MessageSquare, label: 'Enquiries', value: stats.total_enquiries || 0, color: '#7C3AED', bg: 'rgba(139,92,246,0.1)' },
          { icon: IndianRupee, label: 'Revenue', value: `₹${(stats.total_revenue || 0).toLocaleString('en-IN')}`, color: '#16A34A', bg: 'rgba(34,197,94,0.1)' },
        ].map((s, i) => (
          <Card key={i} className="rounded-xl border-border/80 bg-white shadow-sm">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: s.bg }}>
                <s.icon className="w-5 h-5" style={{ color: s.color }} />
              </div>
              <div>
                <p className="text-[10px] uppercase font-medium" style={{ color: 'var(--ink-muted)' }}>{s.label}</p>
                <p className="text-lg font-bold" style={{ color: s.color }}>{s.value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-white border">
          <TabsTrigger value="campaigns" className="gap-1">
            <BarChart3 className="w-3.5 h-3.5" /> Campaigns
          </TabsTrigger>
          <TabsTrigger value="gallery" className="gap-1">
            <ImageIcon className="w-3.5 h-3.5" /> All Images
          </TabsTrigger>
          <TabsTrigger value="review" className="gap-1">
            <Eye className="w-3.5 h-3.5" /> Review Queue
          </TabsTrigger>
          <TabsTrigger value="archive" className="gap-1" data-testid="archive-tab">
            <Archive className="w-3.5 h-3.5" /> AI Archive
            {archiveImages.length > 0 && (
              <Badge className="ml-1 text-[9px] px-1 py-0" style={{ backgroundColor: 'var(--brand-gold)', color: 'white' }}>
                {archiveImages.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Campaigns Tab */}
        <TabsContent value="campaigns" className="mt-4">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-48" />)}
            </div>
          ) : campaigns.length === 0 ? (
            <Card className="rounded-xl border-border/80 bg-white">
              <CardContent className="py-12 text-center">
                <Sparkles className="w-12 h-12 mx-auto mb-3" style={{ color: 'rgba(27,42,74,0.15)' }} />
                <p className="text-sm font-medium" style={{ color: 'var(--brand-navy)' }}>No campaigns yet</p>
                <p className="text-xs mt-1" style={{ color: 'var(--ink-muted)' }}>Create your first AI lookbook campaign</p>
                <Button size="sm" className="mt-4 gap-1" onClick={() => setShowCreate(true)}
                  style={{ backgroundColor: 'var(--brand-gold)' }}>
                  <Plus className="w-3.5 h-3.5" /> Create Campaign
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {campaigns.map(campaign => {
                const statusStyle = STATUS_COLORS[campaign.status] || STATUS_COLORS.draft;
                return (
                  <Card key={campaign.id} className="rounded-xl border-border/80 bg-white shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                    data-testid={`campaign-card-${campaign.id}`}>
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-sm font-semibold" style={{ color: 'var(--brand-navy)' }}>
                            {campaign.campaign_name}
                          </CardTitle>
                          <CardDescription className="text-[10px] capitalize">{campaign.campaign_type}</CardDescription>
                        </div>
                        <Badge className="text-[9px] px-1.5 py-0.5 capitalize"
                          style={{ backgroundColor: statusStyle.bg, color: statusStyle.text }}>
                          {campaign.status}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0 space-y-3">
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--surface-2)' }}>
                          <p className="text-lg font-bold" style={{ color: 'var(--brand-navy)' }}>{campaign.total_clients || 0}</p>
                          <p className="text-[9px]" style={{ color: 'var(--ink-muted)' }}>Clients</p>
                        </div>
                        <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--surface-2)' }}>
                          <p className="text-lg font-bold" style={{ color: 'var(--brand-gold)' }}>{campaign.total_images_generated || 0}</p>
                          <p className="text-[9px]" style={{ color: 'var(--ink-muted)' }}>Images</p>
                        </div>
                        <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--surface-2)' }}>
                          <p className="text-lg font-bold text-green-600">{campaign.total_sent || 0}</p>
                          <p className="text-[9px]" style={{ color: 'var(--ink-muted)' }}>Sent</p>
                        </div>
                      </div>
                      {campaign.status === 'draft' && (
                        <Button size="sm" className="w-full gap-1 text-xs" onClick={(e) => { e.stopPropagation(); handleStartGeneration(campaign.id); }}
                          style={{ backgroundColor: 'var(--brand-navy)' }}>
                          <Zap className="w-3.5 h-3.5" /> Start Generation
                        </Button>
                      )}
                      {campaign.status === 'generating' && (
                        <div className="flex items-center justify-center gap-2 py-2">
                          <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--brand-gold)' }} />
                          <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>Generating images...</span>
                        </div>
                      )}
                      {campaign.status === 'failed' && campaign.error && (
                        <div className="p-2 rounded-lg text-xs" style={{ backgroundColor: 'rgba(239,68,68,0.1)' }}>
                          <p className="font-medium text-red-600 flex items-center gap-1">
                            <X className="w-3 h-3" /> Generation Failed
                          </p>
                          <p className="text-red-500 mt-1 line-clamp-2">{campaign.error}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* All Images Tab */}
        <TabsContent value="gallery" className="mt-4">
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {[...Array(12)].map((_, i) => <Skeleton key={i} className="aspect-[3/4]" />)}
            </div>
          ) : images.length === 0 ? (
            <Card className="rounded-xl border-border/80 bg-white">
              <CardContent className="py-12 text-center">
                <ImageIcon className="w-12 h-12 mx-auto mb-3" style={{ color: 'rgba(27,42,74,0.15)' }} />
                <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>No try-on images generated yet</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {images.map((img, idx) => (
                <div 
                  key={img.id} 
                  className="relative group rounded-xl overflow-hidden border border-border/80 bg-white shadow-sm cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02]"
                  data-testid={`tryon-image-${img.id}`}
                >
                  {img.generated_image_url ? (
                    <img 
                      src={img.generated_image_url} 
                      alt="Try-on" 
                      className="w-full aspect-[3/4] object-cover"
                      onClick={() => openLightbox(images.filter(i => i.generated_image_url), images.filter(i => i.generated_image_url).findIndex(i => i.id === img.id))}
                    />
                  ) : (
                    <div className="w-full aspect-[3/4] flex items-center justify-center" style={{ backgroundColor: 'var(--surface-2)' }}>
                      {img.status === 'generating' ? (
                        <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--brand-gold)' }} />
                      ) : img.status === 'failed' ? (
                        <X className="w-6 h-6 text-red-500" />
                      ) : (
                        <ImageIcon className="w-6 h-6" style={{ color: 'var(--ink-muted)' }} />
                      )}
                    </div>
                  )}
                  {/* Overlay with actions */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="absolute top-2 right-2 flex gap-1">
                      <button onClick={(e) => { e.stopPropagation(); handleWhatsAppShare(img); }}
                        className="p-1.5 rounded-full bg-green-500/80 hover:bg-green-500 text-white transition-colors"
                        title="Send via WhatsApp" data-testid={`whatsapp-btn-${img.id}`}>
                        <MessageCircle className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); handleCopyImage(img.generated_image_url); }}
                        className="p-1.5 rounded-full bg-white/20 hover:bg-white/40 text-white transition-colors"
                        title="Copy Image" data-testid={`copy-btn-${img.id}`}>
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center" 
                      onClick={() => img.generated_image_url && openLightbox(images.filter(i => i.generated_image_url), images.filter(i => i.generated_image_url).findIndex(i => i.id === img.id))}>
                      <div className="p-2 rounded-full bg-white/20 backdrop-blur-sm">
                        <Eye className="w-5 h-5 text-white" />
                      </div>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-2">
                      <p className="text-white text-[10px] font-medium truncate">{img.client_name || 'Client'}</p>
                      <p className="text-white/80 text-[9px] truncate capitalize">{img.garment_type?.replace(/_/g, ' ')}</p>
                      {img.quality_rating && (
                        <Badge className="text-[8px] mt-1" style={{
                          backgroundColor: RATING_OPTIONS.find(r => r.value === img.quality_rating)?.color || '#6B7280',
                          color: 'white',
                        }}>
                          {img.quality_rating}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Review Queue Tab */}
        <TabsContent value="review" className="mt-4">
          {(() => {
            const needsReview = images.filter(img => img.status === 'completed' && !img.quality_rating);
            return needsReview.length === 0 ? (
              <Card className="rounded-xl border-border/80 bg-white">
                <CardContent className="py-12 text-center">
                  <Check className="w-12 h-12 mx-auto mb-3 text-green-500" />
                  <p className="text-sm font-medium" style={{ color: 'var(--brand-navy)' }}>All caught up!</p>
                  <p className="text-xs mt-1" style={{ color: 'var(--ink-muted)' }}>No images pending review</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {needsReview.map((img, idx) => (
                  <Card key={img.id} className="rounded-xl border-border/80 bg-white shadow-sm hover:shadow-md transition-shadow">
                    <CardContent className="p-3">
                      <div className="aspect-[3/4] rounded-lg overflow-hidden mb-3 cursor-pointer relative group"
                        onClick={() => openLightbox(needsReview, idx)}>
                        {img.generated_image_url ? (
                          <>
                            <img src={img.generated_image_url} alt="Try-on" className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <div className="p-2 rounded-full bg-white/20 backdrop-blur-sm">
                                <Eye className="w-6 h-6 text-white" />
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: 'var(--surface-2)' }}>
                            <ImageIcon className="w-8 h-8" style={{ color: 'var(--ink-muted)' }} />
                          </div>
                        )}
                      </div>
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-xs font-medium" style={{ color: 'var(--brand-navy)' }}>
                          {img.client_name || 'Client'}
                        </p>
                        <div className="flex gap-1">
                          <button onClick={() => handleWhatsAppShare(img)}
                            className="p-1 rounded bg-green-100 hover:bg-green-200 text-green-700 transition-colors" title="WhatsApp">
                            <MessageCircle className="w-3 h-3" />
                          </button>
                          <button onClick={() => handleCopyImage(img.generated_image_url)}
                            className="p-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors" title="Copy">
                            <Copy className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                      <p className="text-[10px] mb-1 capitalize" style={{ color: 'var(--ink-muted)' }}>
                        {img.garment_type?.replace(/_/g, ' ')}
                      </p>
                      <p className="text-[10px] mb-3 line-clamp-1" style={{ color: 'var(--ink-muted)' }}>
                        {img.fabric_description}
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {RATING_OPTIONS.map(opt => (
                          <Button key={opt.value} variant="outline" size="sm" className="text-[9px] px-2 py-1 h-auto"
                            onClick={() => handleRateImage(img.id, opt.value)}
                            style={{ borderColor: opt.color, color: opt.color }}>
                            {opt.label}
                          </Button>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            );
          })()}
        </TabsContent>

        {/* AI Archive Tab */}
        <TabsContent value="archive" className="mt-4" data-testid="archive-tab-content">
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {[...Array(6)].map((_, i) => <Skeleton key={i} className="aspect-[3/4]" />)}
            </div>
          ) : archiveImages.length === 0 ? (
            <Card className="rounded-xl border-border/80 bg-white">
              <CardContent className="py-12 text-center">
                <Archive className="w-12 h-12 mx-auto mb-3" style={{ color: 'rgba(27,42,74,0.15)' }} />
                <p className="text-sm font-medium" style={{ color: 'var(--brand-navy)' }}>Archive is empty</p>
                <p className="text-xs mt-1" style={{ color: 'var(--ink-muted)' }}>
                  Quick Try images without a client are automatically saved here
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {archiveImages.map((img) => (
                <div key={img.id} className="relative group rounded-xl overflow-hidden border border-amber-200/60 bg-white shadow-sm"
                  data-testid={`archive-image-${img.id}`}>
                  {img.image_data ? (
                    <img src={img.image_data} alt="Archived" className="w-full aspect-[3/4] object-cover" />
                  ) : (
                    <div className="w-full aspect-[3/4] flex items-center justify-center" style={{ backgroundColor: 'var(--surface-2)' }}>
                      <Archive className="w-6 h-6" style={{ color: 'var(--ink-muted)' }} />
                    </div>
                  )}
                  {/* Overlay with actions */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-2">
                    <p className="text-white text-[10px] capitalize mb-1">{img.garment_type?.replace(/_/g, ' ')}</p>
                    <p className="text-white/70 text-[9px] truncate mb-2">{img.fabric_code || img.fabric_description || 'Custom'}</p>
                    <div className="flex gap-1">
                      <Button size="sm" className="flex-1 h-7 text-[10px] gap-1"
                        style={{ backgroundColor: 'var(--brand-gold)' }}
                        onClick={() => { setAssignArchiveId(img.id); setShowAssign(true); }}
                        data-testid={`assign-archive-${img.id}`}>
                        <UserPlus className="w-3 h-3" /> Assign
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 px-2 border-white/30 text-white hover:bg-white/10"
                        onClick={() => handleWhatsAppShare({ ...img, client_name: 'there' })}
                        title="WhatsApp">
                        <MessageCircle className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 px-2 border-red-400/50 text-red-400 hover:bg-red-500/20"
                        onClick={() => handleDeleteArchive(img.id)}
                        data-testid={`delete-archive-${img.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  {/* Archive badge */}
                  <div className="absolute top-1.5 left-1.5">
                    <Badge className="text-[8px] px-1 py-0" style={{ backgroundColor: 'rgba(245,158,11,0.9)', color: 'white' }}>
                      <Archive className="w-2.5 h-2.5 mr-0.5" /> Unclaimed
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create Campaign Dialog */}
      <Dialog open={showCreate} onOpenChange={(open) => { setShowCreate(open); if (!open) { setSelectedClients([]); setClientSearch(''); } }}>
        <DialogContent className="max-w-lg" data-testid="create-campaign-dialog">
          <DialogHeader>
            <DialogTitle style={{ color: 'var(--brand-navy)' }}>Create Lookbook Campaign</DialogTitle>
            <DialogDescription>Generate personalized try-on images for selected clients</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Campaign Name</Label>
              <Input value={newCampaign.campaign_name} onChange={e => setNewCampaign({ ...newCampaign, campaign_name: e.target.value })}
                placeholder="e.g. Winter Wedding Collection 2026" className="mt-1" data-testid="campaign-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Campaign Type</Label>
                <Select value={newCampaign.campaign_type} onValueChange={v => setNewCampaign({ ...newCampaign, campaign_type: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="seasonal">Seasonal Collection</SelectItem>
                    <SelectItem value="new_arrivals">New Arrivals</SelectItem>
                    <SelectItem value="occasion">Occasion-Based</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Looks per Client (max {generationLimits.max_looks_per_client})</Label>
                <Select value={String(Math.min(newCampaign.looks_per_client, generationLimits.max_looks_per_client))}
                  onValueChange={v => {
                    const val = parseInt(v);
                    if (val > generationLimits.max_looks_per_client) {
                      toast.error(`Maximum ${generationLimits.max_looks_per_client} looks per client to maintain quality and relevance.`);
                      setNewCampaign({ ...newCampaign, looks_per_client: generationLimits.max_looks_per_client });
                    } else {
                      setNewCampaign({ ...newCampaign, looks_per_client: val });
                    }
                  }}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: generationLimits.max_looks_per_client }, (_, i) => i + 1).map(n =>
                      <SelectItem key={n} value={String(n)}>{n} look{n > 1 ? 's' : ''}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Client Selection - Searchable Multi-select */}
            <div>
              <Label className="text-xs">Select Clients *</Label>
              <p className="text-[10px] mb-1.5" style={{ color: 'var(--ink-muted)' }}>
                Search and select specific clients for this campaign
              </p>
              
              {/* Selected clients chips */}
              {selectedClients.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {selectedClients.map(c => (
                    <Badge key={c.id} variant="outline" className="gap-1 pl-2 pr-1 py-1 text-xs"
                      style={{ borderColor: 'var(--brand-gold)', color: 'var(--brand-navy)' }}
                      data-testid={`selected-client-${c.id}`}>
                      {c.first_name} {c.last_name}
                      <button onClick={() => removeSelectedClient(c.id)}
                        className="ml-0.5 p-0.5 rounded-full hover:bg-red-100 transition-colors">
                        <X className="w-3 h-3 text-red-500" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
              
              {/* Search input */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--ink-muted)' }} />
                <Input
                  value={clientSearch}
                  onChange={e => setClientSearch(e.target.value)}
                  placeholder="Search clients by name or phone..."
                  className="pl-10 h-10"
                  data-testid="campaign-client-search"
                />
                {clientSearchLoading && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin" style={{ color: 'var(--brand-gold)' }} />
                )}
              </div>
              
              {/* Search results dropdown */}
              {clientSearchResults.length > 0 && (
                <ScrollArea className="mt-1 max-h-[160px] border rounded-lg">
                  {clientSearchResults.map(client => {
                    const isSelected = selectedClients.some(c => c.id === client.id);
                    return (
                      <div key={client.id}
                        className={`px-3 py-2 cursor-pointer flex items-center justify-between transition-colors ${isSelected ? 'bg-amber-50' : 'hover:bg-gray-50'}`}
                        onClick={() => toggleClientSelection(client)}
                        data-testid={`search-result-${client.id}`}>
                        <div>
                          <p className="text-sm font-medium" style={{ color: 'var(--brand-navy)' }}>
                            {client.first_name} {client.last_name}
                          </p>
                          <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>{client.phone}</p>
                        </div>
                        {isSelected && <Check className="w-4 h-4" style={{ color: 'var(--brand-gold)' }} />}
                      </div>
                    );
                  })}
                </ScrollArea>
              )}
              
              <p className="text-[10px] mt-1" style={{ color: 'var(--ink-muted)' }}>
                {selectedClients.length} client{selectedClients.length !== 1 ? 's' : ''} selected
              </p>
            </div>
            
            <div>
              <Label className="text-xs">Occasion Context (optional)</Label>
              <Textarea value={newCampaign.occasion_context} onChange={e => setNewCampaign({ ...newCampaign, occasion_context: e.target.value })}
                placeholder="e.g. Winter weddings, festive season gatherings..." className="mt-1" rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreateCampaign} disabled={creating || selectedClients.length === 0} className="gap-1"
              style={{ backgroundColor: 'var(--brand-gold)' }} data-testid="confirm-create-campaign-btn">
              {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              Create Campaign ({selectedClients.length} clients)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Archive Image to Client Dialog */}
      <Dialog open={showAssign} onOpenChange={(open) => { setShowAssign(open); if (!open) { setAssignSearch(''); setAssignArchiveId(null); } }}>
        <DialogContent className="max-w-sm" data-testid="assign-archive-dialog">
          <DialogHeader>
            <DialogTitle style={{ color: 'var(--brand-navy)' }}>Assign to Client</DialogTitle>
            <DialogDescription>Search for a client to assign this try-on image to their gallery</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--ink-muted)' }} />
              <Input
                value={assignSearch}
                onChange={e => setAssignSearch(e.target.value)}
                placeholder="Search clients..."
                className="pl-10 h-10"
                data-testid="assign-client-search"
              />
            </div>
            {assignSearchLoading && (
              <div className="flex justify-center py-2">
                <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--brand-gold)' }} />
              </div>
            )}
            {assignSearchResults.length > 0 && (
              <ScrollArea className="max-h-[200px]">
                <div className="space-y-1">
                  {assignSearchResults.map(client => (
                    <div key={client.id}
                      className="px-3 py-2.5 rounded-lg cursor-pointer hover:bg-amber-50 flex items-center justify-between transition-colors border border-transparent hover:border-amber-200"
                      onClick={() => handleAssignArchive(client.id)}
                      data-testid={`assign-client-${client.id}`}>
                      <div>
                        <p className="text-sm font-medium" style={{ color: 'var(--brand-navy)' }}>
                          {client.first_name} {client.last_name}
                        </p>
                        <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>{client.phone}</p>
                      </div>
                      {assigning ? (
                        <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--brand-gold)' }} />
                      ) : (
                        <ChevronRight className="w-4 h-4" style={{ color: 'var(--ink-muted)' }} />
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Lightbox */}
      <TryOnImageLightbox
        images={lightboxImages}
        initialIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        onApprove={handleApproveImage}
        onReject={handleRejectImage}
        onRegenerate={handleRegenerateImage}
        onRate={handleRateImage}
      />
    </div>
  );
}
